import React, { FunctionComponent, useEffect, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import Close from '@mui/icons-material/Close';
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grow,
  IconButton,
  Snackbar,
} from '@mui/material';
import { Loader } from 'semantic-ui-react';
import { DeskingOfferHistoryInfo } from '@upstart/harmony/lib/legos/deskingTypes';
import { VersionSnapshots } from 'components/DeskingEditor/types';
import { useFetchingCallbacks } from 'hooks/useFetchingCallbacks';
import { OfferHeader } from './OfferHeader';
import {
  VersionInfo,
  isVersionPropChangeEmpty,
} from './VersionInfo/VersionInfo';
import { UNSAVED_VERSION } from './Constants';
import { RestoreAlertDialog } from './RestoreAlertDIalog';
import { getRestoreMessage, getRestoreType } from './Helper';
import './PencilHistory.scss';

enum AlertType {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

interface PencilHistoryProps {
  pencilHistoryOpened: boolean;
  offerHistory: DeskingOfferHistoryInfo[] | null;
  fetchingOfferHistory: boolean;
  fetchingOfferHistoryError: { status: number; error: string } | null;
  versionSnapshots: VersionSnapshots | null;
  fetchingVersionSnapshots: boolean;
  fetchingVersionSnapshotsError: { status: number; error: string } | null;
  restoringVersion: boolean;
  restoringVersionError: { status: number; error: string } | null;
  closePencilHistory: () => void;
  renderVersionSnapshots: (args: {
    deskingOfferId: string;
    version: number;
  }) => void;
  restoreVersionRequest: (args: {
    deskingOfferId: string;
    version: number;
  }) => void;
  setIsViewingCurrentPencilVersion: (isViewing: boolean) => void;
}

/**
 * This is the sidebar component that slides in from the right side
 * of the screen to display the pencil history.
 * @param param0
 * @returns
 */
export const PencilHistory: FunctionComponent<PencilHistoryProps> = ({
  pencilHistoryOpened,
  offerHistory,
  fetchingOfferHistory,
  fetchingOfferHistoryError,
  versionSnapshots,
  fetchingVersionSnapshots,
  fetchingVersionSnapshotsError,
  restoringVersion,
  restoringVersionError,
  closePencilHistory,
  renderVersionSnapshots,
  restoreVersionRequest,
  setIsViewingCurrentPencilVersion,
}) => {
  const [initialRender, setInitialRender] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [versionDate, setVersionDate] = useState<Date | null>(null);
  const [sortedSnapshots, setSortedSnapshots] = useState<
    DeskingOfferHistoryInfo[]
  >([]);
  const [restoreButtonDisabled, setRestoreButtonDisabled] = useState(true);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState('');

  const [alertType, setAlertType] = useState(AlertType.INFO);
  const [alertMessage, setAlertMessage] = useState('');
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  // ====== Effects ======

  useEffect(() => {
    setInitialRender(pencilHistoryOpened);
  }, [pencilHistoryOpened]);

  useEffect(() => {
    if (
      !fetchingOfferHistory &&
      !fetchingOfferHistoryError &&
      offerHistory &&
      offerHistory.length > 0
    ) {
      const sorted = [...offerHistory].sort((a, b) => b.version - a.version);
      const filtered = sorted.filter(
        (item, index) =>
          index === offerHistory.length - 1 || !isVersionPropChangeEmpty(item),
      );
      setSortedSnapshots(filtered);
      setSelectedVersion(filtered[0].version);
    }
  }, [fetchingOfferHistory, fetchingOfferHistoryError, offerHistory]);

  useEffect(() => {
    if (selectedVersion === null) {
      return;
    }
    const selectedOffer = offerHistory?.find(
      offer => offer.version === selectedVersion,
    );
    if (selectedOffer && !initialRender) {
      setIsViewingCurrentPencilVersion(false);
      renderVersionSnapshots({
        deskingOfferId: selectedOffer.deskingOfferId,
        version: selectedOffer.version,
      });
      const date = new Date(selectedOffer.createdAt);
      setVersionDate(date);
    } else {
      setIsViewingCurrentPencilVersion(true);
      setAlertMessage('Viewing current version');
      setAlertType(AlertType.INFO);
      setIsAlertOpen(true);
      setInitialRender(false);
    }
  }, [selectedVersion]);

  useEffect(() => {
    if (selectedVersion === null) {
      return;
    }
    const restoreType = getRestoreType(sortedSnapshots, selectedVersion);
    const financeType = sortedSnapshots[0].data?.financeType;
    setRestoreMessage(getRestoreMessage(restoreType, financeType));
    setRestoreButtonDisabled(sortedSnapshots[0].version === selectedVersion);
  }, [selectedVersion, sortedSnapshots]);

  useEffect(() => {
    // need to send a resize event to make the double scroll bar show up
    setTimeout(() => window.dispatchEvent(new Event('resize')), 600);
  }, [pencilHistoryOpened]);

  // ====== Fetching Callbacks ======

  const openAlert = (message: string, type: AlertType) => {
    setAlertMessage(message);
    setAlertType(type);
    setIsAlertOpen(true);
  };

  const fetchVersionSuccessCallback = () => {
    const message =
      versionSnapshots &&
      !versionSnapshots.deskingOffers.some(offer => offer.createdAt)
        ? 'Viewing current version'
        : `Viewing ${moment(versionDate).format('MMMM DD, YYYY h:mm A')}`;
    openAlert(message, AlertType.INFO);
  };

  const fetchVersionErrorCallback = () =>
    openAlert('Error loading pencil version', AlertType.ERROR);

  useFetchingCallbacks({
    isFetching: fetchingVersionSnapshots,
    fetchingError: fetchingVersionSnapshotsError,
    successCallback: fetchVersionSuccessCallback,
    errorCallback: fetchVersionErrorCallback,
  });

  const restoreVersionSuccessCallback = () => {
    // close dialog and sidebar
    setSelectedVersion(null);
    setIsViewingCurrentPencilVersion(true);
    closePencilHistory();
    openAlert('Pencil version restored', AlertType.INFO);
  };

  const restoreVersionErrorCallback = () =>
    openAlert('Error restoring pencil version', AlertType.ERROR);

  useFetchingCallbacks({
    isFetching: restoringVersion,
    fetchingError: restoringVersionError,
    successCallback: restoreVersionSuccessCallback,
    errorCallback: restoreVersionErrorCallback,
  });

  // ====== Event Handlers ======

  const handleClose = () => {
    setSelectedVersion(null);
    setIsViewingCurrentPencilVersion(true);
    closePencilHistory();
  };

  const handleRestore = () => {
    const selectedOffer = offerHistory?.find(
      offer => offer.version === selectedVersion,
    );
    if (selectedOffer) {
      setRestoreDialogOpen(false);
      restoreVersionRequest({
        deskingOfferId: selectedOffer.deskingOfferId,
        version: selectedOffer.version,
      });
    }
  };

  const handleCancelRestore = () => {
    setRestoreDialogOpen(false);
  };

  const hasUnsavedVersion =
    sortedSnapshots &&
    sortedSnapshots.length > 0 &&
    sortedSnapshots[0].version === UNSAVED_VERSION;

  return (
    <>
      <div
        className={classNames({
          'pencil-history-container-opened': pencilHistoryOpened,
          'pencil-history-container-closed': !pencilHistoryOpened,
        })}
      >
        <div className="pencil-history-header">
          <div className="pencil-history-header-group">
            <IconButton
              aria-label="close"
              onClick={() => {
                handleClose();
              }}
            >
              <Close fontSize="large" />
            </IconButton>
            <div>Pencil Version History</div>
          </div>
          <div className="pencil-history-header-group">
            <Button
              className="pencil-history-cancel-button"
              size="medium"
              variant="outlined"
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              className="pencil-history-restore-button"
              size="medium"
              variant="contained"
              disabled={restoreButtonDisabled}
              onClick={() => setRestoreDialogOpen(true)}
            >
              Restore
            </Button>
          </div>
        </div>
        {fetchingOfferHistory && (
          <div className="pencil-history-loader-container">
            <Loader size={'medium'} active inline />
          </div>
        )}
        {fetchingOfferHistoryError && (
          <div className="pencil-history-loader-container error">
            Error loading pencil history
          </div>
        )}
        <Backdrop
          sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
          open={restoringVersion}
        >
          <div className="pencil-history-restore-progress">
            <CircularProgress color="inherit" />
            <div>Restoring pencil version...</div>
          </div>
        </Backdrop>
        {!fetchingOfferHistory &&
          !fetchingOfferHistoryError &&
          offerHistory &&
          offerHistory.length > 0 && (
            <>
              <OfferHeader
                offerData={offerHistory[offerHistory.length - 1].data}
              />
              {sortedSnapshots.map((offerHistoryInfo, index) => (
                <VersionInfo
                  key={offerHistoryInfo.id}
                  offerHistoryInfo={offerHistoryInfo}
                  offerHistory={sortedSnapshots}
                  currentVersion={hasUnsavedVersion ? index === 1 : index === 0}
                  isSelected={offerHistoryInfo.version === selectedVersion}
                  setSelectedVersion={setSelectedVersion}
                />
              ))}
            </>
          )}
      </div>
      <RestoreAlertDialog
        open={restoreDialogOpen}
        message={restoreMessage}
        handleRestore={handleRestore}
        handleCancel={handleCancelRestore}
      />
      <Snackbar
        open={isAlertOpen}
        autoHideDuration={4000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        TransitionComponent={Grow}
        onClose={() => setIsAlertOpen(false)}
      >
        <Alert severity={alertType} variant="filled" sx={{ width: '100%' }}>
          <span style={{ fontSize: '14px' }}>{alertMessage}</span>
        </Alert>
      </Snackbar>
    </>
  );
};
